import React from 'react';
import { Link } from 'react-router-dom';
import {
  CpuChipIcon,
  ShieldCheckIcon,
  ServerIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  SparklesIcon,
  DocumentCheckIcon,
  CloudIcon
} from '@heroicons/react/24/outline';
import { SEOHead, seoData } from '../hooks/useSEO';

const Home: React.FC = () => {
  const services = [
    {
      icon: CpuChipIcon,
      title: 'AI Development Services',
      description: 'Advanced AI solutions including real-time voice systems, OpenAI integration, and containerized microservices for enterprise deployment.',
      features: ['Real-Time AI Voice Systems', 'OpenAI GPT-4o Integration', 'Containerized Microservices']
    },
    {
      icon: ShieldCheckIcon,
      title: 'Enterprise Cybersecurity',
      description: 'Comprehensive cybersecurity with proven Fortune 500 methodologies, compliance frameworks, and advanced threat protection.',
      features: ['NIST/DFARS/ISO 27001 Compliance', 'Advanced Penetration Testing', 'Enterprise Vulnerability Management']
    },
    {
      icon: SparklesIcon,
      title: 'Infrastructure Automation',
      description: 'Enterprise-grade automation using SaltStack, CI/CD pipelines, and custom scripting for policy enforcement and optimization.',
      features: ['SaltStack Automation', 'Security Policy Enforcement', 'CI/CD Pipeline Development']
    },
    {
      icon: ServerIcon,
      title: 'Enterprise Infrastructure',
      description: 'Complete infrastructure solutions including large-scale network design, security appliances, and high-availability systems.',
      features: ['Large-Scale Network Design', 'Security Appliance Integration', 'VMware Infrastructure']
    },
    {
      icon: DocumentCheckIcon,
      title: 'Compliance & Risk Management',
      description: 'Comprehensive compliance framework implementation with proven experience in government and enterprise environments.',
      features: ['NIST Framework Implementation', 'Risk Assessment Services', 'Audit Preparation & Support']
    },
    {
      icon: CloudIcon,
      title: 'Cloud Security & Integration',
      description: 'Advanced cloud security architecture, threat modeling, and integration services for multi-platform environments.',
      features: ['Cloud Security Architecture', 'Threat Modeling & Risk Assessment', 'Container Security (Kubernetes)']
    }
  ];

  const testimonials = [
    {
      name: 'Client Representative',
      company: 'Illuminated',
      text: 'I have found TyrantShield Technologies LLC coding abilities quite useful across the board. They have been helping with the code side on our extraction sensor. I cannot wait to get back to pushing the sensor since it has so many applications in our industry.',
      rating: 5
    },
    {
      name: 'Mohammod Zureick',
      company: 'Industry Partner',
      text: 'I stand strongly behind TyrantShield Technologies LLC and their exceptional work ethic and quality. They are very well-rounded with great communication skills. Working with them is like hitting the fast-forward button on development. Highly recommended professionals who integrate seamlessly into any industry.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead {...seoData.home} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-red-600 text-white section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <h1 className="text-3xl md:text-4xl font-heading font-bold leading-tight">
                Enterprise IT & Cybersecurity Cheyenne WY
              </h1>
              <p className="text-lg text-blue-200 mt-2 font-medium">
                TyrantShield Technologies - Your trusted technology partner delivering enterprise-grade solutions
              </p>
              <p className="text-xl text-blue-100 leading-relaxed">
                Your trusted technology partner delivering enterprise-grade IT services, advanced cybersecurity solutions, and comprehensive compliance
                frameworks with methodologies proven in Fortune 500 and government environments.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book" className="btn-primary bg-white text-blue-600 hover:bg-blue-50">
                  Book Consultation
                </Link>
                <Link to="/services" className="btn-outline border-white text-white hover:bg-white hover:text-blue-600">
                  View Services
                </Link>
              </div>
            </div>
            <div className="relative animate-slide-up animation-delay-200">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                      <CheckCircleIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold">Enterprise AI Systems</h2>
                      <p className="text-blue-200 text-sm">Real-time voice AI and containerized microservices</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                      <CheckCircleIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold">
                        <Link to="/hardware-sales" className="hover:text-white transition-colors">
                          Hardware Sales
                        </Link>
                      </h2>
                      <p className="text-blue-200 text-sm">Enterprise servers, networking, security appliances</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                      <CheckCircleIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold">
                        <Link to="/software-licensing" className="hover:text-white transition-colors">
                          Software Licensing
                        </Link>
                      </h2>
                      <p className="text-blue-200 text-sm">Microsoft, security, backup & virtualization software</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                      <CheckCircleIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold">Compliance & Security</h2>
                      <p className="text-blue-200 text-sm">NIST, DFARS, ISO 27001 frameworks</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Professional IT & Cybersecurity Services in Cheyenne, Wyoming
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We deliver enterprise-grade technology solutions to Wyoming businesses with proven methodologies from
              Fortune 500 and government environments, specializing in IT support, advanced
              cybersecurity, and comprehensive compliance frameworks for Cheyenne area companies.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="card p-8 animate-slide-up" style={{ animationDelay: `${index * 200}ms` }}>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <service.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 mb-4">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center space-x-2 text-sm text-slate-700">
                          <CheckCircleIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services" className="btn-primary inline-flex items-center space-x-2">
              <span>View All Wyoming IT Services</span>
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="bg-blue-50 section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Serving Cheyenne, Wyoming & Surrounding Areas
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
              As a locally-based IT company and cybersecurity provider, we're proud to serve businesses throughout Wyoming with enterprise-grade technology solutions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Cheyenne Metro Area</h3>
              <p className="text-slate-600">
                Comprehensive IT support and cybersecurity services for businesses in Cheyenne and the greater metro area.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Statewide Wyoming</h3>
              <p className="text-slate-600">
                Remote IT support, cybersecurity consulting, and compliance services available throughout Wyoming.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">24/7 Remote Support</h3>
              <p className="text-slate-600">
                Emergency IT support and cybersecurity response available around the clock for Wyoming businesses.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-blue-100 max-w-2xl mx-auto">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Local Wyoming Business Focus</h3>
              <p className="text-slate-600">
                We understand the unique technology challenges facing Wyoming businesses, from energy sector compliance
                to rural connectivity solutions. Our team provides personalized service with enterprise-grade expertise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Content Section for SEO */}
      <section className="bg-white section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-8 text-center">
              Why Choose TyrantShield Technologies for Your Wyoming Business IT Needs?
            </h2>
            
            <div className="prose prose-lg max-w-none text-slate-700 space-y-6">
              <p className="text-xl leading-relaxed">
                TyrantShield Technologies LLC stands as Wyoming's premier IT company and cybersecurity provider, delivering enterprise-grade technology solutions to businesses throughout Cheyenne and the greater Wyoming region. With over two decades of combined experience in Fortune 500 environments and government contracting, our team brings unparalleled expertise to Wyoming's unique business landscape.
              </p>
              
              <h3 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">
                Comprehensive IT Services Tailored for Wyoming Industries
              </h3>
              
              <p>
                Our comprehensive suite of IT services addresses the specific challenges facing Wyoming businesses, from energy sector compliance requirements to the unique connectivity challenges of rural operations. We understand that Wyoming companies need technology partners who can deliver enterprise-grade solutions while understanding local business dynamics and regulatory requirements.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-8">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h4 className="text-xl font-semibold text-slate-900 mb-3">Enterprise Cybersecurity Excellence</h4>
                  <p className="text-slate-700">
                    Our cybersecurity services go beyond basic protection, implementing comprehensive security frameworks including NIST SP 800-53, DFARS compliance, and ISO 27001 standards. We've successfully secured Fortune 500 environments and bring that same level of protection to Wyoming businesses of all sizes.
                  </p>
                </div>
                
                <div className="bg-red-50 p-6 rounded-lg">
                  <h4 className="text-xl font-semibold text-slate-900 mb-3">Advanced AI Integration</h4>
                  <p className="text-slate-700">
                    Stay ahead of the competition with our cutting-edge AI development services, including real-time voice systems, OpenAI GPT-4o integration, and containerized microservices. We help Wyoming businesses leverage artificial intelligence to automate processes and enhance customer experiences.
                  </p>
                </div>
              </div>
              
              <h3 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">
                Proven Track Record in Government and Enterprise Environments
              </h3>
              
              <p>
                Our team's extensive background includes successful implementations in high-security government environments, Fortune 500 corporations, and critical infrastructure projects. This experience translates directly to superior service delivery for Wyoming businesses, ensuring your technology infrastructure meets the highest standards of security, reliability, and performance.
              </p>
              
              <p>
                We specialize in complex compliance requirements that many Wyoming businesses face, particularly those in the energy sector or working with government contracts. Our expertise in NERC CIP compliance, DFARS implementation, and NIST framework adoption ensures your business meets all regulatory requirements while maintaining operational efficiency.
              </p>
              
              <h3 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">
                Local Expertise with Global Capabilities
              </h3>
              
              <p>
                Based in Cheyenne, Wyoming, we combine deep local market knowledge with global technology expertise. We understand the unique challenges facing Wyoming businesses, from seasonal operational variations in the energy sector to the connectivity challenges of rural locations. Our solutions are designed specifically for the Wyoming business environment while leveraging the latest global technology trends.
              </p>
              
              <p>
                Our comprehensive service portfolio includes everything from basic IT support and network management to advanced cybersecurity implementations and AI-powered business solutions. Whether you're a small Cheyenne startup or a large Wyoming energy company, we have the expertise and resources to support your technology needs at every stage of growth.
              </p>
              
              <h3 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">
                Why Wyoming Businesses Choose TyrantShield Technologies
              </h3>
              
              <p>
                Our commitment to excellence extends beyond technical expertise. We provide personalized service that understands the unique challenges of Wyoming's business environment, from harsh weather conditions affecting infrastructure to the specialized compliance requirements of the energy sector. Our team has successfully implemented technology solutions for businesses ranging from small Cheyenne startups to large-scale energy operations across the state.
              </p>
              
              <p>
                We maintain strategic partnerships with leading technology vendors, ensuring our Wyoming clients have access to the latest enterprise-grade solutions at competitive pricing. Our local presence in Cheyenne means faster response times for critical issues, while our remote capabilities ensure comprehensive support throughout Wyoming's vast geography.
              </p>
              
              <div className="bg-gradient-to-r from-blue-600 to-red-600 text-white p-8 rounded-lg my-8">
                <h4 className="text-2xl font-semibold mb-4">Ready to Transform Your Wyoming Business?</h4>
                <p className="text-lg mb-6">
                  Join the growing number of Wyoming businesses that trust TyrantShield Technologies for their IT and cybersecurity needs. Our proven methodologies, local expertise, and commitment to excellence make us the ideal technology partner for your business growth and security requirements.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/book" className="btn-primary bg-white text-blue-600 hover:bg-blue-50 text-center">
                    Schedule Your Free Consultation
                  </Link>
                  <Link to="/services" className="btn-outline border-white text-white hover:bg-white hover:text-blue-600 text-center">
                    Explore Our Services
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              What Wyoming Businesses Say About Our IT Services
            </h2>
            <p className="text-xl text-slate-600">
              Don't just take our word for it - hear from our satisfied Wyoming clients
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="card p-6 animate-fade-in" style={{ animationDelay: `${index * 200}ms` }}>
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-slate-700 mb-4 italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <div className="font-semibold text-slate-900">{testimonial.name}</div>
                  <div className="text-sm text-slate-600">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-red-600 text-white section-padding">
        <div className="container-max text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Ready to Transform Your Wyoming Business with Professional IT Services?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Get started today with a free consultation from Cheyenne's premier IT company. Let us help you leverage technology
            to drive your Wyoming business forward.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" className="btn-primary bg-white text-blue-600 hover:bg-blue-50">
              Schedule Free Consultation
            </Link>
            <Link to="/contact" className="btn-outline border-white text-white hover:bg-white hover:text-blue-600">
              Contact Us Today
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;